class LopderCartCrossSell extends HTMLElement {
  static get observedAttributes() {
    return ['data-product-ids'];
  }

  constructor() {
    super();

    this.requestId = 0;
    this.abortController = null;

    this.handleVariantChange =
      this.handleVariantChange.bind(this);

    this.handleDismiss =
      this.handleDismiss.bind(this);
  }

  connectedCallback() {
    this.addEventListener(
      'change',
      this.handleVariantChange
    );

    this.addEventListener(
      'click',
      this.handleDismiss
    );

    // 如果客户在当前页面已经关闭推荐区，
    // 购物车重新渲染时保持关闭
    if (window.__lopderCartUpsellDismissed) {
      this.hidden = true;
      return;
    }

    this.loadRecommendations();
  }

  disconnectedCallback() {
    this.removeEventListener(
      'change',
      this.handleVariantChange
    );

    this.removeEventListener(
      'click',
      this.handleDismiss
    );

    if (this.abortController) {
      this.abortController.abort();
    }
  }

  attributeChangedCallback(
    name,
    oldValue,
    newValue
  ) {
    if (
      name === 'data-product-ids' &&
      oldValue !== newValue &&
      this.isConnected
    ) {
      if (window.__lopderCartUpsellDismissed) {
        this.hidden = true;
        return;
      }

      this.loadRecommendations();
    }
  }

  getProductIds() {
    return [
      ...new Set(
        (this.dataset.productIds || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean)
      ),
    ];
  }

  async loadRecommendations() {
    const list = this.querySelector(
      '[data-cart-upsell-list]'
    );

    if (!list) return;

    if (window.__lopderCartUpsellDismissed) {
      this.hidden = true;
      return;
    }

    const productIds = this.getProductIds();

    if (!productIds.length) {
      this.hidden = true;
      list.innerHTML = '';
      return;
    }

    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController =
      new AbortController();

    const currentRequest =
      ++this.requestId;

    this.hidden = true;
    list.innerHTML = '';

    const existingProductIds =
      new Set(productIds);

    const addedRecommendationIds =
      new Set();

    const recommendations = [];

    const root =
      window.Shopify?.routes?.root || '/';

    for (const productId of productIds) {
      if (recommendations.length >= 2) {
        break;
      }

      try {
        const url = new URL(
          `${root}recommendations/products`,
          window.location.origin
        );

        url.searchParams.set(
          'product_id',
          productId
        );

        url.searchParams.set(
          'limit',
          '4'
        );

        url.searchParams.set(
          'intent',
          'complementary'
        );

        url.searchParams.set(
          'section_id',
          'cart-complementary-products'
        );

        const response = await fetch(
          url.toString(),
          {
            headers: {
              Accept: 'text/html',
            },
            credentials: 'same-origin',
            signal:
              this.abortController.signal,
          }
        );

        if (!response.ok) {
          continue;
        }

        const html =
          await response.text();

        if (
          currentRequest !== this.requestId
        ) {
          return;
        }

        const doc =
          new DOMParser().parseFromString(
            html,
            'text/html'
          );

        const cards =
          doc.querySelectorAll(
            '[data-cart-upsell-item]'
          );

        for (const card of cards) {
          const recommendedId =
            card.dataset.productId;

          if (!recommendedId) {
            continue;
          }

          if (
            existingProductIds.has(
              recommendedId
            )
          ) {
            continue;
          }

          if (
            addedRecommendationIds.has(
              recommendedId
            )
          ) {
            continue;
          }

          addedRecommendationIds.add(
            recommendedId
          );

          recommendations.push(card);

          if (
            recommendations.length >= 2
          ) {
            break;
          }
        }
      } catch (error) {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[Lopder cart cross-sell] Recommendation load failed:',
            error
          );
        }
      }
    }

    if (
      currentRequest !== this.requestId
    ) {
      return;
    }

    if (
      window.__lopderCartUpsellDismissed
    ) {
      this.hidden = true;
      return;
    }

    if (!recommendations.length) {
      this.hidden = true;
      list.innerHTML = '';
      return;
    }

    const fragment =
      document.createDocumentFragment();

    recommendations.forEach((card) => {
      fragment.appendChild(
        document.importNode(
          card,
          true
        )
      );
    });

    list.replaceChildren(fragment);

    this.hidden = false;
  }

  handleDismiss(event) {
    const closeButton =
      event.target.closest(
        '[data-cart-upsell-close]'
      );

    if (!closeButton) return;

    event.preventDefault();
    event.stopPropagation();

    // 记录当前页面已经关闭推荐区
    window.__lopderCartUpsellDismissed = true;

    // 停止尚未完成的推荐请求
    if (this.abortController) {
      this.abortController.abort();
    }

    // 防止旧请求回来以后重新显示
    this.requestId += 1;

    // 隐藏整个推荐区
    this.hidden = true;
  }

  handleVariantChange(event) {
    const select =
      event.target.closest(
        '[data-upsell-variant]'
      );

    if (!select) return;

    const card =
      select.closest(
        '[data-cart-upsell-item]'
      );

    if (!card) return;

    const selectedOption =
      select.options[
        select.selectedIndex
      ];

    if (!selectedOption) return;

    const variantId =
      selectedOption.value;

    const price =
      selectedOption.dataset.price || '';

    const comparePrice =
      selectedOption.dataset.comparePrice || '';

    const imageUrl =
      selectedOption.dataset.image || '';

    const hiddenVariantInput =
      card.querySelector(
        'input[name="id"][ref="variantId"]'
      );

    if (hiddenVariantInput) {
      hiddenVariantInput.value =
        variantId;
    }

    const currentPrice =
      card.querySelector(
        '[data-upsell-current-price]'
      );

    if (currentPrice && price) {
      currentPrice.textContent = price;
    }

    const comparePriceElement =
      card.querySelector(
        '[data-upsell-compare-price]'
      );

    if (comparePriceElement) {
      if (comparePrice) {
        comparePriceElement.textContent =
          comparePrice;

        comparePriceElement.hidden =
          false;
      } else {
        comparePriceElement.textContent =
          '';

        comparePriceElement.hidden =
          true;
      }
    }

    if (imageUrl) {
      const image =
        card.querySelector(
          '[data-upsell-image]'
        );

      if (image) {
        image.src = imageUrl;
        image.removeAttribute('srcset');
      }

      const addToCartComponent =
        card.querySelector(
          'add-to-cart-component'
        );

      if (addToCartComponent) {
        addToCartComponent.dataset
          .productVariantMedia =
          imageUrl;
      }
    }

    const productUrl =
      card.dataset.productUrl;

    if (productUrl) {
      const url = new URL(
        productUrl,
        window.location.origin
      );

      url.searchParams.set(
        'variant',
        variantId
      );

      card
        .querySelectorAll(
          '[data-upsell-product-link]'
        )
        .forEach((link) => {
          link.href =
            url.pathname +
            url.search;
        });
    }
  }
}

if (
  !customElements.get(
    'lopder-cart-cross-sell'
  )
) {
  customElements.define(
    'lopder-cart-cross-sell',
    LopderCartCrossSell
  );
}